import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import VilatteProductCard from './VilatteProductCard';
import { VilatteCartLink } from './VilatteCart';

import { decodeFilters, filterUrl } from './vilatteFilterUrls';

const VilattePage = () => {
  const [rawParams] = useSearchParams();
  const { filterType, filterSlug } = useParams();
  const location = useLocation();
  const params = useMemo(() => decodeFilters(rawParams, filterType, filterSlug), [rawParams, filterType, filterSlug]);
  const setParams = (next, options) => navigate(filterUrl(next), options);
  const navigate = useNavigate();
  const [query, setQuery] = useState(params.get('q') || '');
  const [data, setData] = useState({ products: [], facets: {}, page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestKey = params.toString();
  useEffect(() => {
    if (params.has('product')) return;
    const canonical = filterUrl(params);
    if (location.pathname + location.search !== canonical) navigate(canonical, { replace: true });
  }, [params, location.pathname, location.search, navigate]);
  useEffect(() => { window.scrollTo(0,0); }, []);
  useEffect(() => setQuery(params.get('q') || ''), [params]);

  useEffect(() => {
    const legacyProduct = params.get('product');
    if (legacyProduct) navigate(`/vilatte/${encodeURIComponent(legacyProduct)}`, { replace: true });
  }, [navigate, params]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      fetch(`/api/vilatte/products?${requestKey}`, { signal: controller.signal })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Не удалось загрузить каталог')))
        .then((payload) => { if (!controller.signal.aborted) { setData(payload); setError(''); } })
        .catch((requestError) => { if (requestError.name !== 'AbortError') setError(requestError.message); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 120);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [requestKey]);

  const update = (name, value, resetPage = true) => {
    const next = new URLSearchParams(params);
    value ? next.set(name, value) : next.delete(name);
    if (name === 'section') ['category', 'collection', 'size', 'height'].forEach(key => next.delete(key));
    if (resetPage) next.delete('page');
    setParams(next, { replace: true });
  };
  const submitSearch = (event) => { event.preventDefault(); update('q', query); };
  const facets = data.facets || {};
  const filtersActive = useMemo(() => ['q', 'section', 'category', 'collection', 'size', 'height', 'new', 'min_price', 'max_price'].some((key) => params.has(key)), [params]);
  const resetCatalog = () => { setQuery(''); setParams({}); };
  const activeFilters = [['q','Поиск'],['section','Раздел'],['category','Категория'],['collection','Коллекция'],['size','Размер'],['height','Рост'],['min_price','Цена от'],['max_price','Цена до']].filter(([key])=>params.get(key)).map(([key,label])=>`${label}: ${params.get(key)}`);
  if(params.get('new')==='1') activeFilters.push('Только новинки');

  return (
    <main className="vilatte-page">
      <section className="container vilatte-page-intro">
        <VilatteCartLink />
        <p className="vilatte-kicker">КАТАЛОГ SHOPTOP BRANDS</p>
        <h1>VILATTE по оптовым ценам</h1>
        <p>Современная одежда с продуманной посадкой. Здесь показаны текущие размеры, остатки и окончательная цена до Новосибирска.</p>
      </section>

      <section className="container vilatte-catalog" aria-label="Каталог VILATTE">
        <nav className="vilatte-section-nav" aria-label="Разделы VILATTE">
          <button type="button" aria-pressed={!filtersActive} onClick={resetCatalog}>Все товары</button>
          {(facets.sections || []).map(section => <button type="button" key={section} aria-pressed={params.get('section') === section} onClick={() => update('section', section)}>{section}</button>)}
          <button type="button" aria-pressed={params.get('new') === '1'} onClick={() => update('new', params.get('new') === '1' ? '' : '1')}>Только новинки</button>
        </nav>
        <form className="vilatte-search" onSubmit={submitSearch}>
          <label><span>Поиск</span><input value={query} onChange={(event) => { setQuery(event.target.value); update('q', event.target.value); }} placeholder="Артикул, название, цвет, состав" /></label>
          <button type="submit">Найти</button>
        </form>
        <div className="vilatte-filters">
          <Filter label="Раздел" value={params.get('section') || ''} options={facets.sections} onChange={(value) => update('section', value)} />
          <Filter label="Категория" value={params.get('category') || ''} options={facets.categories} onChange={(value) => update('category', value)} />
          <Filter label="Коллекция" value={params.get('collection') || ''} options={facets.collections} onChange={(value) => update('collection', value)} />
          <Filter label="Размер" value={params.get('size') || ''} options={facets.sizes} onChange={(value) => update('size', value)} />
          <Filter label="Рост" value={params.get('height') || ''} options={facets.heights} onChange={(value) => update('height', value)} />
          <label><span>Цена от</span><input inputMode="numeric" value={params.get('min_price') || ''} onChange={(event) => update('min_price', event.target.value)} /></label>
          <label><span>Цена до</span><input inputMode="numeric" value={params.get('max_price') || ''} onChange={(event) => update('max_price', event.target.value)} /></label>
          <label><span>Сортировка</span><select value={params.get('sort') || 'newest'} onChange={(event) => update('sort', event.target.value)}><option value="newest">Сначала новинки</option><option value="price_asc">Сначала дешевле</option><option value="price_desc">Сначала дороже</option></select></label>
          {filtersActive && <button className="vilatte-reset" type="button" onClick={() => { setQuery(''); setParams({}); }}>Сбросить</button>}
        </div>
        {filtersActive && <p className="vilatte-active-filters">{activeFilters.join(' · ')} <button type="button" className="vilatte-reset" onClick={resetCatalog}>Показать все товары</button></p>}
        <div className="vilatte-results-head"><strong>{data.total || 0} товаров</strong><span>Актуальные остатки</span></div>
        {loading ? <div className="vilatte-state">Загружаем актуальные остатки…</div> : error ? <div className="vilatte-state is-error">{error}</div> : data.products.length ? (
          <div className="vilatte-grid">{data.products.map((product) => <VilatteProductCard key={product.external_id} product={product} onOpen={() => navigate((product.product_url || `/vilatte/${encodeURIComponent(product.external_id)}`))} />)}</div>
        ) : <div className="vilatte-state"><h2>Подходящих товаров пока нет</h2><p>Измените фильтры — каталог обновляется автоматически.</p></div>}
        {data.pages > 1 && <nav className="vilatte-pagination" aria-label="Страницы каталога"><button type="button" disabled={data.page <= 1} onClick={() => update('page', String(data.page - 1), false)}>Назад</button><span>{data.page} / {data.pages}</span><button type="button" disabled={data.page >= data.pages} onClick={() => update('page', String(data.page + 1), false)}>Дальше</button></nav>}
      </section>
    </main>
  );
};

const Filter = ({ label, value, options = [], onChange }) => <label><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}><option value="">Все</option>{options.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>;

export default VilattePage;

